import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Table, Dropdown, Space } from "antd";
import { PlusOutlined, MoreOutlined, ExclamationCircleFilled, AppstoreOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { API_URL, TOKEN } from "../../../variables/constants";
import SideBar from "../../../components/SideBar";
import NavBar from "../../../components/NavBar";
import Footer from "../../../components/Footer";
import AddValeurPointIP from "./AddValeurPointIP";


const { confirm } = Modal;

const ValeurPointIP = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${TOKEN}`,
      "Content-Type": "application/json",
    }),
    []
  );

  const fetchAll = () => {
    axios
      .get(`${API_URL}/api/valeur-point-ip`, { headers })
      .then((res) => {
        const sorted = [...res.data].sort((a, b) => {
          if (a.valeurPointIpId != null && b.valeurPointIpId != null)
            return b.valeurPointIpId - a.valeurPointIpId;
          return 0;
        });
        setData(sorted);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Erreur lors du chargement des configurations.");
      });
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleShow = (isEdit = false, item = null) => {
    setEditMode(isEdit);
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedItem(null);
  };

  const handleAdd = (created) => {
    setData((prev) =>
      [created, ...prev].sort((a, b) => {
        if (a.valeurPointIpId != null && b.valeurPointIpId != null)
          return b.valeurPointIpId - a.valeurPointIpId;
        return 0;
      })
    );
  };

  const handleUpdate = (updated) => {
    setData((prev) =>
      prev
        .map((it) => (it.valeurPointIpId === updated.valeurPointIpId ? updated : it))
        .sort((a, b) => {
          if (a.valeurPointIpId != null && b.valeurPointIpId != null)
            return b.valeurPointIpId - a.valeurPointIpId;
          return 0;
        })
    );
  };

  const confirmDelete = (id) => {
    confirm({
      title: "Supprimer cette configuration ?",
      icon: <ExclamationCircleFilled />,
      content: "Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk: () => doDelete(id),
    });
  };

  const doDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/valeur-point-ip/delete/${id}`, { headers });
      setData((prev) => prev.filter((it) => it.valeurPointIpId !== id));
      toast.success("Supprimé avec succès.");
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la suppression.");
    }
  };

  const columns = [
    {
      title: "Tranches Taux IP",
      dataIndex: "trancheTauxIp",
      key: "trancheTauxIp",
      render: (arr) => (arr && arr.length ? arr.join(", ") : "-"),
      sorter: (a, b) =>
        (a.trancheTauxIp?.length || 0) - (b.trancheTauxIp?.length || 0),
    },
    {
      title: "Tranches Âge Blessé",
      dataIndex: "trancheAgeBlesse",
      key: "trancheAgeBlesse",
      render: (arr) => (arr && arr.length ? arr.join(", ") : "-"),
      sorter: (a, b) =>
        (a.trancheAgeBlesse?.length || 0) - (b.trancheAgeBlesse?.length || 0),
    },
    {
      title: "Valeur du point IP",
      dataIndex: "valeur",
      key: "valeur",
      render: (val) => {
        if (val === null || val === undefined) return "-";
        const numeric = Number(val);
        return Number.isFinite(numeric) ? numeric.toLocaleString("fr-FR") : val;
      },
      sorter: (a, b) => Number(a.valeur || 0) - Number(b.valeur || 0),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => {
        const menuItems = [
          { key: "edit", label: "Modifier", onClick: () => handleShow(true, record) },
          { key: "delete", label: "Supprimer", danger: true, onClick: () => confirmDelete(record.valeurPointIpId) },
        ];
        return (
          <Dropdown
            menu={{
              items: menuItems.map((it) => ({
                key: it.key,
                label: (
                  <span
                    style={it.danger ? { color: "#cf1322" } : {}}
                    onClick={(e) => {
                      e.preventDefault();
                      it.onClick();
                    }}
                  >
                    {it.label}
                  </span>
                ),
              })),
            }}
            placement="bottomRight"
            trigger={["click"]}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu="PARAMETRES_SINISTRES" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <Space style={{ marginBottom: 16 }} wrap>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => handleShow(false, null)}
                >
                  Ajouter
                </Button>
                <Button
                  icon={<AppstoreOutlined />}
                  onClick={() => navigate("/parametres/ajouter-valeur-point-ip/v2")}
                >
                  Nouvelle vue (V2)
                </Button>
              </Space>

              <div className="card">
                <h5 className="card-header">Valeur Point IP</h5>
                <div className="table-responsive text-nowrap p-3">
                  <Table
                    rowKey={(r) => r.valeurPointIpId}
                    dataSource={data}
                    columns={columns}
                    pagination={{ pageSize: 10, showSizeChanger: true }}
                    size="middle"
                  />
                </div>
              </div>
            </div>

            <Footer />
          </div>
        </div>
      </div>

      <AddValeurPointIP
        show={showModal}
        handleClose={handleClose}
        editMode={editMode}
        selectedItem={selectedItem}
        items={data}
        onAdd={handleAdd}
        onUpdate={handleUpdate}
      />
    </div>
  );
};

export default ValeurPointIP;
